// frontend/src/features/categories/NewCategoryForm.js
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave } from "@fortawesome/free-solid-svg-icons";
import { Container, Form, Button, Row, Spinner, Alert, InputGroup } from 'react-bootstrap';
import { useAddNewCategoryMutation } from "./categoriesApiSlice";

const NewCategoryForm = () => {
    // addNewCategory mutation hook
    const [addNewCategory, {
        isLoading,
        isSuccess,
        isError,
        error
    }] = useAddNewCategoryMutation();

    const navigate = useNavigate();

    // Form data state
    const [categoryData, setCategoryData] = useState({
        name: '',
        description: '',
        order: '', // Default to empty string for number input
        isActive: true, // Default to active
    });

    const [selectedFile, setSelectedFile] = useState(null); // State to hold the actual file object
    const [filePreview, setFilePreview] = useState(null); // State for image preview
    const [formError, setFormError] = useState(''); // To display API errors or form-wide errors
    const [errors, setErrors] = useState({}); // For client-side validation errors

    // Effect to handle success and reset form/navigate
    useEffect(() => {
        if (isSuccess) {
            setCategoryData({ // Reset form data on success
                name: '',
                description: '',
                order: '',
                isActive: true,
            });
            setSelectedFile(null);
            setFilePreview(null);
            setErrors({});
            setFormError('');
            navigate('/dashboard/categories'); // Navigate back to categories list
        }
    }, [isSuccess, navigate]);

    // Effect to display API error
    useEffect(() => {
        if (isError) {
            setFormError(error?.data?.message || 'Failed to create category.');
        }
    }, [isError, error]);

    // Input change handler
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setCategoryData(prevData => ({
            ...prevData,
            [name]: type === 'checkbox' ? checked : value
        }));
        // Clear error for the field when user starts typing/changing
        if (errors[name]) {
            setErrors(prevErrors => ({ ...prevErrors, [name]: '' }));
        }
        // Clear form-wide error
        setFormError('');
    };

    // File input change handler
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        setSelectedFile(file);
        if (file) {
            setFilePreview(URL.createObjectURL(file));
        } else {
            setFilePreview(null);
        }
        setErrors(prevErrors => ({ ...prevErrors, file: '' })); // Clear file error
        setFormError('');
    };

    // Client-side validation - All checks enabled
    const validateForm = () => {
        const newErrors = {};
        if (!categoryData.name.trim()) {
            newErrors.name = 'Category name is required.';
        }
        if (categoryData.description.length > 200) {
            newErrors.description = 'Description cannot exceed 200 characters.';
        }
        if (categoryData.order === '' || isNaN(categoryData.order) || parseInt(categoryData.order) < 0) {
            newErrors.order = 'Order number must be a non-negative number and is required.';
        }
        // ✅ Category image is now required for validation
        if (!selectedFile) {
            newErrors.file = 'Category image is required.';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Form submission handler
    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormError(''); // Clear previous form errors

        if (!validateForm()) {
            return;
        }

        try {
            const formData = new FormData();
            formData.append('name', categoryData.name);
            formData.append('description', categoryData.description);
            formData.append('order', categoryData.order.toString());
            formData.append('isActive', categoryData.isActive.toString()); // Convert boolean to string

            if (selectedFile) {
                formData.append('file', selectedFile); // Append the actual file
            }

            await addNewCategory(formData).unwrap();
        } catch (err) {
            // Error handled by useEffect, but log for debugging
            console.error('Failed to create category:', err);
        }
    };

    // canSave now checks if all required fields are filled (name, order, and file)
    const canSave = [categoryData.name, categoryData.order, selectedFile].every(Boolean) && !isLoading;

    const content = (
        <>
            <Container className="mt-5">
                <Row className="justify-content-md-center">
                    <h2 className="mb-4 text-center">Create New Category</h2>
                    {formError && <Alert variant="danger" className="mb-3">{formError}</Alert>}
                    <Form onSubmit={handleSubmit} className="p-4 border rounded shadow-sm form-container">
                        <Form.Group className='formGroup mb-3'>
                            <Form.Label htmlFor="categoryName">Category Name<sup>*</sup>:</Form.Label>
                            <Form.Control
                                type="text"
                                id="categoryName"
                                name='name'
                                placeholder='e.g., General, Satsang'
                                value={categoryData.name}
                                onChange={handleInputChange}
                                required
                                aria-describedby="name-error"
                            />
                            {errors.name && <span id="name-error" className="error">{errors.name}</span>}
                        </Form.Group>

                        <Form.Group className='formGroup mb-3'>
                            <Form.Label htmlFor="categoryDescription">Description:</Form.Label>
                            <Form.Control
                                as='textarea'
                                id="categoryDescription"
                                name='description'
                                placeholder='A brief description of the category'
                                value={categoryData.description}
                                onChange={handleInputChange}
                                maxLength={200}
                                aria-describedby="description-error"
                            />
                            {errors.description && <span id="description-error" className="error">{errors.description}</span>}
                        </Form.Group>

                        <Form.Group className='formGroup mb-3'>
                            <Form.Label htmlFor="categoryOrder">Order Number<sup>*</sup>:</Form.Label>
                            <Form.Control
                                type="number"
                                id="categoryOrder"
                                name='order'
                                placeholder='Order for display of categories'
                                value={categoryData.order}
                                onChange={handleInputChange}
                                min="0"
                                required // Made required as per validation
                                aria-describedby="order-error"
                            />
                            {errors.order && <span id="order-error" className="error">{errors.order}</span>}
                        </Form.Group>

                        <Form.Group className='formGroup mb-3'>
                            <Form.Check
                                type="checkbox"
                                id="categoryIsActive"
                                name="isActive"
                                label="Active"
                                checked={categoryData.isActive}
                                onChange={handleInputChange}
                            />
                        </Form.Group>

                        <Form.Group className='formGroup mb-3'>
                            <Form.Label htmlFor="categoryImage">Category Image/Icon<sup>*</sup>:</Form.Label>
                            <Form.Control
                                type="file"
                                id="categoryImage"
                                name="file"
                                accept="image/*"
                                onChange={handleFileChange}
                                required // Made required as per validation
                                aria-describedby="file-error"
                            />
                            {errors.file && <span id="file-error" className="error">{errors.file}</span>}
                            {filePreview && (
                                <div className="mt-2">
                                    <img src={filePreview} alt="Image Preview" style={{ maxWidth: '100px', maxHeight: '100px' }} />
                                </div>
                            )}
                        </Form.Group>

                        <Button variant="primary" type="submit" disabled={!canSave || isLoading}>
                            <FontAwesomeIcon icon={faSave} className="me-2" />
                            Create Category {isLoading ? (
                                <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    className="ms-2"
                                />
                            ) : null}
                        </Button>
                    </Form>
                </Row>
            </Container>
        </>
    );

    return content;
};

export default NewCategoryForm;